import { styled } from '@atom-web/core'
import { Button, Flex, Row, Text } from '@atom-web/micros'
import { useAmp } from 'next/amp'
import { Fragment, useEffect, useState } from 'react'

import Portal from '../../../components/HOC/Portal'
import { useDidUpdate } from '../../../hooks/useDidUpdate'

const SeoDescWrapper = styled('div', {
	width: '100%',
	position: 'relative',
	overflow: 'hidden',
	transition: 'all 0.2s ease-in-out',

	'& h1': {
		fontWeight: '$bold',
		fontSize: '$4',
		lineHeight: '$3',
		color: '$grey12',
		marginTop: 0,
		marginBottom: '$1',

		'@sm': {
			fontSize: '$5',
		},
	},

	'& div': {
		display: 'block',
	},

	'& p': {
		display: 'block',
	},

	'@sm': {
		th: {
			fontSize: '14px',
			border: '1px solid #E4EBF1',
		},
		td: {
			fontSize: '14px',
			border: '1px solid #E4EBF1',
		},
	},

	th: {
		backgroundColor: '#F3F6F9',
		padding: '12px 21px 12px 21px',
	},

	td: {
		padding: '12px 21px 12px 21px',
	},

	'& .table-holder': {
		overflowX: 'auto',
		overflowY: 'hidden',
	},
})

const SeoTextSection = styled('div', {
	padding: '0 4px 0',
	textAlign: 'left',

	h2: {
		fontSize: '17px',
		fontWeight: '$bold',
		lineHeight: '$2',
		color: '$grey12',
		marginTop: 0,
		marginBottom: '$2',

		'@lg': {
			fontSize: '18px',
			marginTop: '$2',
		},
	},
	h3: {
		fontSize: '16px',
		fontWeight: '$bold',
		lineHeight: '22px',
		color: '$grey12',
		marginTop: '0px',
		marginBottom: '$2',
	},
	h4: {
		fontSize: '15px',
		fontWeight: '$bold',
		lineHeight: '22px',
		color: '$grey12',
		marginTop: '0px',
		marginBottom: '$2',
	},
	h5: {
		fontSize: '14px',
		fontWeight: '$bold',
		lineHeight: '20px',
		color: '$grey12',
		marginTop: '0px',
		marginBottom: '$2',
	},
	h6: {
		fontSize: '13px',
		fontWeight: '$bold',
		lineHeight: '20px',
		color: '$grey12',
		marginTop: '0px',
		marginBottom: '$2',
	},
	p: {
		color: '#777',
		marginBottom: '16px',
		fontSize: '16px',
		lineHeight: 1.23,
		letterSpacing: '-0.25px',
	},
	li: {
		fontSize: '14px',
		fontWeight: '$normal',
		lineHeight: '20px',
		color: '#555555',
	},
	ul: {
		fontSize: '14px',
		fontWeight: '$normal',
		lineHeight: '20px',
		color: '#555555',
	},
	span: {
		fontSize: '14px',
		fontWeight: '$normal',
		lineHeight: '20px',
		color: '#555555',
	},
	div: {
		fontSize: '14px',
		fontWeight: '$normal',
		lineHeight: '20px',
		color: '#555555',
	},
	'@lg': {
		paddingLeft: 0,
		paddingRight: 0,
	},
})

// ${({ showFullDesc, noShadow }) =>
// 	!showFullDesc &&
// 	!noShadow &&
// 	css`
// 		&::after {
// 			content: '';
// 			position: absolute;
// 			top: 50%;
// 			left: 0;
// 			width: 100%;
// 			height: 50%;
// 			background: linear-gradient(0, #f6f6f6, transparent);

// 			${above('tablet')} {
// 				background: linear-gradient(0, #ffffff, transparent);
// 			}
// 		}
// 	`};

const ReadMore = styled('span', {
	display: 'inline-block',
	fontSize: '16px',
	textAlign: 'right',
	color: '#555555',
	cursor: 'pointer',
	position: 'absolute',
	bottom: 0,
	right: 0,
	backgroundColor: '$white',
	padding: '0px 0 0 12px',
	textDecoration: 'underline',
	'@sm': {
		width: '100%',
		fontWeight: 600,
		padding: '12px 12px 16px 0px',
	},
})

const SEOBlock = ({
	seoH1Title,
	seoContent,
	noShadow = false,
	height = '140px',
	smHeight = '140px',
	isMobile,
	openState = false,
	onToggle,
}) => {
	const isAmp = useAmp()

	const onShowFullDesc = e => {
		e && e.preventDefault()

		onToggle && onToggle(!openState)
		// setShowFullDesc(!showFullDesc)
	}

	useDidUpdate(() => {
		if (!openState) {
			onClickViewAll()
		}
	}, [openState])

	const onClickViewAll = () => {
		const target = '#packages-cards'
		var element = document.querySelector(target)
		var headerOffset = isMobile ? 0 : 100
		var elementPosition = element?.getBoundingClientRect()?.top
		var offsetPosition = elementPosition + window.pageYOffset - headerOffset

		window.scrollTo({
			top: offsetPosition,
			behavior: 'smooth',
		})
	}

	return (
		<Fragment>
			<SeoDescWrapper
				className="seo-short-desc"
				css={{
					height: openState ? `auto` : height ? height : '120px',
					'@sm': {
						height: openState ? `auto` : smHeight ? smHeight : '140px',
					},
				}}
			>
				{seoH1Title ? (
					<h2
						style={{
							fontSize: '20px',
							fontWeight: 600,
							color: '#333333',
							marginTop: 0,
							marginBottom: 4,
						}}
					>
						{seoH1Title}
					</h2>
				) : null}

				{seoContent ? (
					<SeoTextSection
						className="seo-text"
						noShadow={noShadow}
						showFullDesc={openState}
						dangerouslySetInnerHTML={{ __html: seoContent }}
					/>
				) : null}
				{!isAmp && seoContent && !openState ? (
					<ReadMore onClick={onShowFullDesc}>Read more</ReadMore>
				) : null}
			</SeoDescWrapper>
			<Portal>
				<Flex
					align={'center'}
					justify="center"
					css={{
						position: 'fixed',
						left: 0,
						right: 0,
						zIndex: '$max',
						transition: 'all 0.6s ease-in-out',
						backgroundColor: 'rgba(0, 0, 0, 0.72)',
						bottom: openState ? '144px' : '-100%',
						margin: '0 auto',
						padding: '$2',
						borderRadius: 0,
						height: 70,
						width: '100%',
						'@sm': {
							height: 64,
							width: 360,
							borderRadius: '$2',
							padding: '$4',
							margin: '40px auto',
							bottom: openState ? 72 : '-100%',
						},
					}}
				>
					<Row
						css={{ gap: '20px', width: '100%' }}
						align="center"
						justify={{
							'@initial': 'center',
							'@sm': 'start',
						}}
					>
						<Button
							primary
							gradient
							wide="lg"
							weight="bold"
							onClick={onClickViewAll}
						>
							View all Packages
						</Button>
						<Text
							css={{
								color: '$grey02',
								fontSize: '$2',
								lineHeight: '$2',
								fontWeight: '$bold',
							}}
						>
							or
						</Text>
						<Text
							css={{
								color: '$white',
								fontSize: '$2',
								lineHeight: '$2',
								fontWeight: '$bold',
								textDecoration: 'underline',
								cursor: 'pointer',
							}}
							onClick={onShowFullDesc}
						>
							Read less
						</Text>
					</Row>
				</Flex>
			</Portal>
		</Fragment>
	)
}

export default SEOBlock
